import Cookies from 'js-cookie'
import { useInfiniteQuery } from 'react-query'
import React from 'react'
import classNames from 'classnames'
import { NewsDatum, TNewsWidgetData } from '../types'
import TruncateText from './TruncateText'
import SpinnerLoader from '@/components/Common/header/spinner-loader'
import PrimaryCard from '@/components/Common/PrimaryCard'
import { indianStocksApiBaseUrl } from '@/root/api/apiConstants'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'

export default React.memo(function NewsWidget({
  data,
  childClass,
  headingClassName,
}: {
  data: TNewsWidgetData
  childClass?: string
  headingClassName?: string
}) {
  const {
    data: paginateApiData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery<{ news?: TNewsWidgetData }>(
    'news-paginated',
    async ({ pageParam = data.metadata?.paginated_url }) => {
      const token = Cookies.get('token')
      return fetchRequest({
        url: `${indianStocksApiBaseUrl}/${pageParam}`,
        config: {
          headers: {
            Platform: 'web',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        },
      })
    },
    {
      getNextPageParam(lastPage) {
        if (lastPage.news?.metadata?.paginated_url)
          return lastPage.news.metadata.paginated_url
        else return undefined
      },
      initialData: () => {
        return {
          pageParams: [data.metadata?.paginated_url],
          pages: [{ news: data }],
        }
      },
      staleTime: Infinity,
    },
  )
  const dataAggregated = paginateApiData?.pages
    .flatMap(page => {
      if (page.news?.data) return page.news.data
      return null
    })
    .filter(Boolean) as NewsDatum[] | undefined

  return (
    <PrimaryCard childClass={childClass}>
      <h2
        className={classNames(
          'text-brand-black text-xl font-medium',
          headingClassName,
        )}
      >
        {data.heading}
      </h2>
      {!!dataAggregated?.length &&
        dataAggregated.map((item, idx) => {
          if (!(item.heading && item.sub_heading)) return null
          return (
            <div
              className="border-b-1 border-brand-grey-background flex w-full flex-col gap-1 py-4"
              key={item.heading}
            >
              <h3 className="font-medium">{item.heading}</h3>

              {idx < 3 ? (
                <span className="text-brand-grey-base text-sm">
                  {item.sub_heading}
                </span>
              ) : (
                <TruncateText
                  text={item.sub_heading}
                  sliceVal={160}
                  className="text-brand-grey-base text-sm"
                />
              )}
              <span className="text-brand-grey-dark text-xs font-light">
                {item.timestamp}
              </span>
            </div>
          )
        })}
      {isFetchingNextPage && (
        <div>
          <SpinnerLoader />
        </div>
      )}
      {hasNextPage && !isFetchingNextPage && (
        <div
          className="text-brand-blue-azureRadiance mt-2 w-full cursor-pointer text-center text-sm"
          onClick={() => {
            void fetchNextPage()
          }}
        >
          View More
        </div>
      )}
    </PrimaryCard>
  )
})
